import React, { useEffect, useMemo, useState, useRef } from "react";
import "./css/main.css";
import { QRCodeSVG } from "qrcode.react";
import { RotateCw } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-regular-svg-icons";

// QR 페이지: 큰 QR + 이름 + 30초 카운트 프로그레스바 + 안내 문구
export default function Qr() {
    // 이름은 서버/로컬에서 가져온 값 사용
    const [name, setName] = useState("");
    const [qrSize, setQrSize] = useState(220);
    const qrContainerRef = useRef(null);
    const [level, setLevel] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // 등급에 따른 배경 이미지 설정
    const getBackgroundImage = () => {
        const userLevel = level || Number(localStorage.getItem("hb_userLevel")) || 3;
        if (userLevel === 1) {
            return "/img/bg_img_golden.png";
        } else if (userLevel === 2) {
            return "/img/bg_img_blue.png";
        } else {
            return "/img/sample img.png";
        }
    };

    useEffect(() => {
        setIsLoading(true);
        const n =
            localStorage.getItem("hb_serverName") ||
            localStorage.getItem("hb_googleName") ||
            "";
        setName(n || "이름");
        setIsLoading(false);

        // 등급 정보 가져오기
        const savedLevel = localStorage.getItem("hb_userLevel");
        if (savedLevel) {
            setLevel(Number(savedLevel));
        } else {
            // 등급이 없으면 서버에서 가져오기
            const loadLevel = async () => {
                const API_BASE_URL =
                    process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api";
                const email = localStorage.getItem("hb_serverEmail") ||
                    localStorage.getItem("hb_googleEmail") ||
                    "";

                if (email) {
                    try {
                        const res = await fetch(`${API_BASE_URL}/auth/user-by-email?email=${encodeURIComponent(email)}`, {
                            method: "GET",
                        });
                        if (res.ok) {
                            const data = await res.json();
                            const user = data.user || {};
                            if (user.level != null) {
                                setLevel(user.level);
                                localStorage.setItem("hb_userLevel", String(user.level));
                            }
                        }
                    } catch (e) {
                        console.error("Failed to load level", e);
                    }
                }
            };
            loadLevel();
        }
    }, []);

    // QR 코드 크기를 컨테이너에 맞춰 조절
    useEffect(() => {
        const updateQrSize = () => {
            if (qrContainerRef.current) {
                const containerWidth = qrContainerRef.current.offsetWidth;
                const containerHeight = qrContainerRef.current.offsetHeight;
                const size = Math.min(containerWidth, containerHeight) * 0.9;
                setQrSize(Math.max(100, Math.min(220, size)));
            }
        };

        updateQrSize();
        window.addEventListener('resize', updateQrSize);
        return () => window.removeEventListener('resize', updateQrSize);
    }, []);

    // QR 값: 고정 URL (요청: https://hb.re.kr/)
    const qrValue = useMemo(() => "https://hb.re.kr/", []);

    // 30초 카운트다운
    const DURATION = 30;
    const [remaining, setRemaining] = useState(DURATION);
    const [active, setActive] = useState(true);
    const [dotIndex, setDotIndex] = useState(0); // 현재 활성화된 점 인덱스 (0, 1, 2)

    // 30초 카운트다운 (0.1초 단위로 부드럽게 감소)
    useEffect(() => {
        if (!active) return;

        const start = Date.now();
        const timer = setInterval(() => {
            const elapsedSec = (Date.now() - start) / 1000;
            const next = Math.max(0, DURATION - elapsedSec);
            setRemaining(next);
            if (next <= 0) {
                clearInterval(timer);
                setActive(false);
                setDotIndex(0); // 타이머 종료 시 점 인덱스 초기화
            }
        }, 100); // 0.1초 간격으로 업데이트

        return () => clearInterval(timer);
    }, [active]);

    // 점 순환 애니메이션 (1초마다)
    useEffect(() => {
        if (!active) {
            setDotIndex(0); // 비활성화 시 점 인덱스 초기화
            return;
        }

        const dotTimer = setInterval(() => {
            setDotIndex((prev) => (prev + 1) % 3); // 0 -> 1 -> 2 -> 0 순환
        }, 1000); // 1초마다 점 이동

        return () => clearInterval(dotTimer);
    }, [active]);

    // 0에서 30까지 채워지도록 (경과 시간 기준)
    const progressRatio = (DURATION - remaining) / DURATION;

    const resetTimer = () => {
        setRemaining(DURATION);
        setActive(true);
        setDotIndex(0); // 타이머 리셋 시 점 인덱스 초기화
    };

    const isDisabled = !active;

    return (
        <div className="container-wrap main-wrap">
            <img src={getBackgroundImage()} className="main-background-image" />
            <div className="main-viewport">
                <header className="setting-header">
                    <img
                        src="/img/myCert.png"
                        alt="myCert"
                        className="setting-logo"
                    // onClick={() => navigate("/")}
                    />
                </header>
                <main
                    className="main-content qr-page-content"
                >
                    <div className="main-slide" style={{ display: "flex", justifyContent: "center" }}>
                        <div className="main-card main-id-card">
                            <div className="main-card-inner">
                                <div
                                    className="main-card-face main-card-front"
                                    style={{
                                        // 메인 모바일 신분증과 동일한 glass 스타일
                                        background: "rgba(255, 255, 255, 0.4)",
                                        backdropFilter: "blur(5px) saturate(1.4)",
                                        WebkitBackdropFilter: "blur(5px) saturate(1.4)",
                                        border: "1px solid rgba(255, 255, 255, 0.7)",
                                        boxShadow: "0 0px 10px rgba(15, 23, 42, 0.3)",
                                    }}
                                >
                                    <div className="main-id-card-content" style={{ justifyContent: "center" }}>
                                        {/* QR 영역 - 프로필 사진 영역과 동일한 박스 안에 QR 배치 */}
                                        <div className="main-id-photo-container" ref={qrContainerRef} style={{ overflow: "hidden", flexShrink: 1 }}>
                                            <div
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    position: "relative",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        opacity: isDisabled ? 0.3 : 1,
                                                        transition: "opacity 0.2s ease",
                                                        width: "100%",
                                                        height: "100%",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    <QRCodeSVG
                                                        value={qrValue}
                                                        size={qrSize}
                                                        level="H"
                                                        includeMargin={false}
                                                        fgColor="#000000"
                                                        bgColor="#ffffff"
                                                        style={{
                                                            maxWidth: "100%",
                                                            maxHeight: "100%",
                                                            width: "auto",
                                                            height: "auto"
                                                        }}
                                                    />
                                                </div>

                                                {/* 비활성 오버레이 */}
                                                {isDisabled && (
                                                    <button
                                                        type="button"
                                                        onClick={resetTimer}
                                                        style={{
                                                            position: "absolute",
                                                            inset: 0,
                                                            border: "none",
                                                            background: "rgba(255, 255, 255, 0.75)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            cursor: "pointer",
                                                        }}
                                                        aria-label="QR 다시 보기"
                                                    >
                                                        <RotateCw size={40} color="#111827" strokeWidth={2.4} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* 이름 */}
                                        {isLoading ? (
                                            <div className="main-id-name-skeleton"></div>
                                        ) : (
                                            <div className="main-id-name">
                                                {(name || "이름").split('').map((char, index, array) => (
                                                    <span key={index} className="main-id-name-char" style={{ marginRight: index < array.length - 1 ? '0.4em' : '0' }}>
                                                        {char}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        {/* 30초 카운트 + 시계 아이콘 + 점 3개 */}
                                        <div
                                            style={{
                                                marginTop: 12,
                                                width: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: "8px",
                                            }}
                                        >
                                            <div style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: "8px",
                                            }}>
                                                <FontAwesomeIcon
                                                    icon={faClock}
                                                    style={{
                                                        fontSize: 20,
                                                        color: "#000"
                                                    }}
                                                />
                                                <span style={{
                                                    fontSize: 18,
                                                    color: "#000",
                                                    fontWeight: 500,
                                                }}>
                                                    남은시간 <span style={{ color: "#ef4444" }}>{Math.ceil(remaining)}초</span>
                                                </span>
                                            </div>
                                            {/* 점 3개 */}
                                            <div style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: "9px",
                                            }}>
                                                {[0, 1, 2].map((index) => (
                                                    <div
                                                        key={index}
                                                        className="qr-timer-dot"
                                                        style={{
                                                            backgroundColor: active && dotIndex === index ? "#1e3a8a" : "#9ca3af",
                                                            transition: "background-color 0.3s ease",
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        </div>

                                        {/* 안내 문구 */}
                                        <div
                                            style={{
                                                marginTop: 18,
                                                fontSize: 13,
                                                color: "#4b5563",
                                                textAlign: "center",
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            접수처에 신분증을 보여주세요.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>

                <div style={{ height: 100 }} />
            </div>
        </div>
    );
}

